import { useState } from "react";

export interface Notice {
  text: string;
  isError: boolean;
}

/**
 * A single piece of state for "the outcome of the last save attempt on
 * this form" — success and error are mutually exclusive by construction
 * (setting one replaces the other), so a caller can never end up with a
 * stale success message still showing after a later error, or vice versa.
 * Pair with `<NoticeBanner notice={notice} />` (components/ui.tsx), which
 * picks green (SuccessBanner) or red (ErrorBanner) based on `isError` —
 * never render a save confirmation through ErrorBanner directly.
 *
 *   const { notice, showSuccess, showError } = useNotice();
 *   onSuccess: () => showSuccess("Saved."),
 *   onError: (err) => showError(err instanceof ApiError ? err.message : "Could not save."),
 *   <NoticeBanner notice={notice} />
 */
export function useNotice(): {
  notice: Notice | null;
  showSuccess: (text: string) => void;
  showError: (text: string) => void;
  clear: () => void;
} {
  const [notice, setNotice] = useState<Notice | null>(null);
  return {
    notice,
    showSuccess: (text: string) => setNotice({ text, isError: false }),
    showError: (text: string) => setNotice({ text, isError: true }),
    clear: () => setNotice(null),
  };
}
